import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { pluralize } from 'ember-inflector';
import set from 'ember-set-helper/helpers/set';
import { gt } from 'ember-truth-helpers';
import SelectRecurringInvites from 'garaje/components/invites/select-recurring-invites';
import SendNotificationModal from 'garaje/components/invites/send-notification-modal';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type Invite from 'garaje/models/invite';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import { reads } from 'macro-decorators';
import type { ValueOf } from 'type-fest';

const OPTIONS = { ...RECURRING_OPTIONS, ALL: undefined };

interface DeleteInvitesConfirmationSignature {
  Args: {
    /**
     * action to close the modal
     */
    close: () => void;
    /**
     * task performed when confirming
     */
    onSubmit: (scope: ValueOf<typeof RECURRING_OPTIONS>) => unknown;
    invites?: Invite[];
    showCount?: boolean;
    hasRecurringInvite?: boolean;
    isMultiLocationEdit?: boolean;
    includesMultiLocationInvites?: boolean;
    modelName?: string;
  };
}

export default class DeleteInvitesConfirmation extends Component<DeleteInvitesConfirmationSignature> {
  @service declare featureFlags: FeatureFlagsService;

  @tracked sendNotificationCallback: (() => unknown) | undefined;
  @tracked scope: ValueOf<typeof RECURRING_OPTIONS> = OPTIONS.THIS;

  @reads('args.modelName', 'invite') modelName!: string;
  @reads('args.invites', []) invites!: Invite[];

  get totalInvites(): number {
    return this.invites.length;
  }

  get shouldSendNotificationCallback(): boolean {
    const isFromEmployeeScreening = this.invites.every((invite) => invite.isFromEmployeeScreening);
    const isFromWalkup = this.invites.every((invite) => invite.isFromWalkUp);

    return !isFromEmployeeScreening && !isFromWalkup;
  }

  @action
  onSelectScope(scope: ValueOf<typeof RECURRING_OPTIONS>): void {
    this.scope = scope;
  }

  onSubmitTask = dropTask(async (e: Event) => {
    e.preventDefault();
    if (this.shouldSendNotificationCallback) {
      this.sendNotificationCallback = () => this.args.onSubmit(this.scope);
    } else {
      await this.args.onSubmit(this.scope);
      this.args.close();
    }
  });

  <template>
    {{#if this.sendNotificationCallback}}
      <SendNotificationModal
        @callback={{this.sendNotificationCallback}}
        @onClickBack={{set this "sendNotificationCallback" undefined}}
        @close={{@close}}
        @invites={{this.invites}}
        @action="delete"
        @scope={{this.scope}}
        @prompt="Would you like to send a cancellation email to your guest?"
      />
    {{else}}
      <ModalDialog @clickOutsideToClose={{false}} @targetAttachment="center" @translucentOverlay={{true}}>
        <FocusTrapper>
          <div data-test-delete-confirmation-modal class="p-16">
            <h1 data-test-header>Delete
              {{#if @hasRecurringInvite}}recurring{{/if}}
              {{pluralize this.totalInvites this.modelName withoutCount=true}}</h1>
            <form {{on "submit" this.onSubmitTask.perform}}>
              {{#if @hasRecurringInvite}}
                <SelectRecurringInvites
                  @options={{OPTIONS}}
                  @scope={{this.scope}}
                  @onChange={{this.onSelectScope}}
                  @modelName={{this.modelName}}
                />
              {{else}}
                <p class="mt-8">
                  Are you sure you want to delete
                  {{if (gt this.totalInvites 1) "these" "this"}}
                  {{pluralize this.totalInvites "invite" withoutCount=true}}?
                  {{#if @includesMultiLocationInvites}}
                    {{if
                      (gt this.totalInvites 1)
                      "One or more of these invites is multi-location."
                      "This invite is multi-location."
                    }}
                    Deleting will affect multiple workspaces and cannot be undone.
                  {{else}}
                    {{if
                      @isMultiLocationEdit
                      "This applies to all attached locations and cannot be undone."
                      "This cannot be undone."
                    }}
                  {{/if}}
                </p>
              {{/if}}
              <ButtonGroup @reverse={{true}} class="pt-8 flex justify-end">
                <button
                  class="btn btn--small btn--brand {{if this.onSubmitTask.isRunning 'disabled'}}"
                  disabled={{this.onSubmitTask.isRunning}}
                  type="submit"
                  data-test-delete-confirm
                >
                  {{#if this.onSubmitTask.isRunning}}
                    Deleting...
                  {{else}}
                    Delete
                    {{if @showCount (pluralize this.totalInvites "invite")}}
                  {{/if}}
                </button>
                <button
                  class="btn btn--small btn--hoverFill {{if this.onSubmitTask.isRunning 'disabled'}}"
                  disabled={{this.onSubmitTask.isRunning}}
                  {{on "click" @close}}
                  type="button"
                  data-test-delete-cancel
                >
                  Cancel
                </button>
              </ButtonGroup>
            </form>
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
